interface ShrinkOptions {
  /** Longest edge of the stored picture, in pixels. */
  maxEdge: number;
  /** Past this, re-encode as JPEG instead of PNG. */
  pngBudget: number;
}

/** A QR is read from its pattern; 640px is more than a camera needs for one. */
const QR: ShrinkOptions = { maxEdge: 640, pngBudget: 120 * 1024 };

/**
 * A product picture fills a tile about 160px wide, so 320 covers a 2× screen.
 *
 * Smaller than the QR on purpose: a catalogue has one per product and they are
 * fetched together, so each one's size is multiplied by the whole shelf.
 */
const PRODUCT: ShrinkOptions = { maxEdge: 320, pngBudget: 40 * 1024 };

/** The shop's bank QR. */
export const toQrDataUrl = (file: File): Promise<string> => shrink(file, QR);

/** One product's picture. */
export const toProductDataUrl = (file: File): Promise<string> => shrink(file, PRODUCT);

/**
 * Turns a picked file into a small `data:` URL the server will accept.
 *
 * A phone camera hands over three or four megabytes, which becomes five in
 * base64 — over a shop's connection that is a minute of waiting and a refusal
 * at the end. Downscaling in the browser costs nothing that matters at the
 * sizes these are displayed.
 *
 * PNG first, because one common case is a screenshot — flat colour, hard edges,
 * exactly what PNG is good at and what JPEG smears. A photograph compresses
 * badly as PNG, so anything over the budget is re-encoded as JPEG at a quality
 * that still leaves a QR's modules crisp.
 */
async function shrink(file: File, { maxEdge, pngBudget }: ShrinkOptions): Promise<string> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));

    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas 2d context unavailable');
    // Flattened onto white. A transparent PNG — a QR cut out of a banking app,
    // a product shot with its background removed — would otherwise take on
    // whatever it is dropped onto, and both end up on a white bill or a white
    // tile anyway.
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const png = canvas.toDataURL('image/png');
    if (bytesOf(png) <= pngBudget) return png;
    return canvas.toDataURL('image/jpeg', 0.92);
  } finally {
    bitmap.close();
  }
}

/** Decoded size of a base64 data URL, without decoding it. */
function bytesOf(dataUrl: string): number {
  const encoded = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const padding = encoded.endsWith('==') ? 2 : encoded.endsWith('=') ? 1 : 0;
  return Math.floor((encoded.length * 3) / 4) - padding;
}
